// @flow weak

import localforage from 'localforage';
import * as R from 'ramda';

import { ApolloService } from '~/services';
import { AceService, HttpService } from './';
import { getLanguageFromLocale, getLocaleFromLanguage } from '~/helpers';

import {
  MESSAGE_CHANGE_EMAIL,
  MESSAGE_CHANGE_PASSWORD,
  MESSAGE_DISABLE_SPINNER,
  MESSAGE_GET_AUTH_TOKENS,
  MESSAGE_GO_TO_ROUTER,
  MESSAGE_GET_ELEVATE_TOKENS,
  MESSAGE_OPEN_URL_IN_BROWSER,
  MESSAGE_REFRESH_ROUTER,
  MESSAGE_REMOVE_HTTP_HEADER_AUTHORIZATION_TOKEN,
  MESSAGE_REQUEST_HTTP,
  MESSAGE_RESPOND_HTTP,
  MESSAGE_RESTART_ROUTER,
  MESSAGE_SET_AUTH_TOKENS,
  MESSAGE_SET_ELEVATE_TOKENS,
  MESSAGE_SET_LOCALE,
  MESSAGE_SET_HTTP_HEADER_ACCEPT_LANGUAGE,
  MESSAGE_SET_HTTP_HEADER_AUTHORIZATION,
  MESSAGE_SETUP_MFA,
  MESSAGE_SHOW_NOTIFICATION,
  MESSAGE_SIGN_IN,
  MESSAGE_SIGN_OUT,
  ROUTER_ACTION_REFRESH,
  ROUTER_ACTION_RESTART,
  STORAGE_KEY_DRAFT_ID,
  STORAGE_KEY_LOCALE,
} from '~/helpers';

import {
  MUTATION_ROUTER_TRIGGER_ACTION,
  MUTATION_ROUTER_TRIGGER_GO_TO,
  QUERY_ROUTER_TRIGGER,
} from '../gql';

import {
  DRAFT,
  CREATE_NOTIFICATION,
  ELEVATE_TOKENS,
  ELEVATE_TOKENS_MUTATION,
} from '~/gql';

const aceHandlers = {
  [MESSAGE_SET_AUTH_TOKENS]: ({ authTokens }) =>
    AceService.resolveGetAuthTokens(authTokens),
  [MESSAGE_SET_ELEVATE_TOKENS]: async ({ elevateTokens }) => {
    const id = await localforage.getItem(STORAGE_KEY_DRAFT_ID);

    await Promise.all([
      localforage.removeItem(STORAGE_KEY_DRAFT_ID),
      ApolloService.client.mutate({
        mutation: ELEVATE_TOKENS_MUTATION,
        refetchQueries: [
          {
            query: DRAFT,
            variables: { id },
          },
          {
            query: ELEVATE_TOKENS,
          },
        ],
        variables: {
          values: {
            accessToken: elevateTokens.access_token,
            idToken: elevateTokens.id_token,
          },
        },
      }),
    ]);
  },
};

const appHandlers = {
  [MESSAGE_GO_TO_ROUTER]: async ({ pathname }) =>
    await ApolloService.client.mutate({
      mutation: MUTATION_ROUTER_TRIGGER_GO_TO,
      refetchQueries: [{ query: QUERY_ROUTER_TRIGGER }],
      variables: {
        pathname,
      },
    }),
  [MESSAGE_REFRESH_ROUTER]: async () =>
    await ApolloService.client.mutate({
      mutation: MUTATION_ROUTER_TRIGGER_ACTION,
      refetchQueries: [{ query: QUERY_ROUTER_TRIGGER }],
      variables: {
        action: ROUTER_ACTION_REFRESH,
      },
    }),
  [MESSAGE_RESTART_ROUTER]: async () =>
    await ApolloService.client.mutate({
      mutation: MUTATION_ROUTER_TRIGGER_ACTION,
      refetchQueries: [{ query: QUERY_ROUTER_TRIGGER }],
      variables: {
        action: ROUTER_ACTION_RESTART,
      },
    }),
  [MESSAGE_SET_LOCALE]: async ({ locale }) =>
    await localforage.setItem(
      STORAGE_KEY_LOCALE,
      getLocaleFromLanguage(locale),
    ),
  [MESSAGE_SHOW_NOTIFICATION]: async ({ message, type }) =>
    await ApolloService.client.mutate({
      mutation: CREATE_NOTIFICATION,
      variables: {
        values: { message, type },
      },
    }),
};

const httpHandlers = {
  [MESSAGE_RESPOND_HTTP]: data => HttpService.resolveRequest(data),
};

const postMessage = (message, body) => {
  try {
    window.ReactNativeWebView.postMessage(JSON.stringify({ message, body }));
  } catch (e) {}
};

export const handleMessage = ({ data }, callbacks) => {
  const { message, body = {} } = R.tryCatch(JSON.parse, R.always({}))(data);
  const handlers = {
    ...aceHandlers,
    ...appHandlers,
    ...httpHandlers,
  };

  if (!handlers[message]) {
    return;
  }

  handlers[message](body, callbacks);
};

export const changeEmail = () => postMessage(MESSAGE_CHANGE_EMAIL);
export const changePassword = () => postMessage(MESSAGE_CHANGE_PASSWORD);
export const disableSpinner = () => postMessage(MESSAGE_DISABLE_SPINNER);

export const getAuthTokens = async promise => {
  postMessage(MESSAGE_GET_AUTH_TOKENS);

  return promise;
};

export const getElevateTokens = async draftId => {
  await localforage.setItem(STORAGE_KEY_DRAFT_ID, draftId);
  postMessage(MESSAGE_GET_ELEVATE_TOKENS);
};

export const openUrlInBrowser = url =>
  postMessage(MESSAGE_OPEN_URL_IN_BROWSER, { url });

export const removeHttpHeaderAuthorization = () =>
  postMessage(MESSAGE_REMOVE_HTTP_HEADER_AUTHORIZATION_TOKEN);

export const sendHttpRequest = async ({ id, params, promise }) => {
  postMessage(MESSAGE_REQUEST_HTTP, { id, params });

  return promise;
};

export const setHttpHeaderAcceptLanguage = locale =>
  postMessage(MESSAGE_SET_HTTP_HEADER_ACCEPT_LANGUAGE, {
    locale: getLanguageFromLocale(locale),
  });

export const setHttpHeaderAuthorization = accessToken =>
  postMessage(MESSAGE_SET_HTTP_HEADER_AUTHORIZATION, { accessToken });

export const setLocale = locale =>
  postMessage(MESSAGE_SET_LOCALE, { locale: getLocaleFromLanguage(locale) });

export const setupMfa = () => postMessage(MESSAGE_SETUP_MFA);
export const signIn = () => postMessage(MESSAGE_SIGN_IN);
export const signOut = () => postMessage(MESSAGE_SIGN_OUT);
